import omnivore from '@mapbox/leaflet-omnivore'
import * as turf from '@turf/turf'
import { XMLParser } from 'fast-xml-parser'
import { errors } from '../../services/ruutsApi/errors'
import { dataLayersAreas, dataLayersPoints } from './dataLayers'
import { sortKMLFeaturesByArea } from '../FarmLayers/exportMapeo'
import { GeoJSON } from '../GeoJSON/index'
import { getRandomColor } from '../mapDrawHandle'
import { getPosibleFolderId } from '../mapHandle'
import { Folder, FolderArray, FolderName, plantillaParsing } from './plantillaHandle'

const getParsingFromOmnivore = (data: string): FolderArray => {
  const rawData = omnivore.kml.parse(data)
  const geojsonData = rawData.toGeoJSON() as GeoJSON.FeatureCollection

  const folders: Folder = {}

  dataLayersAreas.forEach(layer => {
    folders[layer.farmLayerType] = {
      features: [],
    }
  })

  dataLayersPoints.forEach(layer => {
    folders[layer.farmLayerType] = {
      features: [],
    }
  })

  const featuresToAssign = geojsonData.features.map((feature, i) => {
    let returnFeature = { ...feature }
    const { type } = feature.geometry

    switch (type) {
      case 'Point':
        returnFeature.properties.featureGroup = 'unassignedPoints'
        returnFeature.properties.id = i
        break
      case 'Polygon':
        returnFeature.properties.area = GeoJSON.readableArea(GeoJSON.area(returnFeature))
        returnFeature.properties.featureGroup = getPosibleFolderId(returnFeature.properties)
        returnFeature.properties.id = i
        break
      case 'MultiPolygon':
        returnFeature.properties.area = GeoJSON.readableArea(GeoJSON.area(returnFeature))
        returnFeature.properties.featureGroup = getPosibleFolderId(returnFeature.properties)
        returnFeature.properties.id = i
        break
      case 'GeometryCollection': {
        const { geometries } = returnFeature.geometry as GeoJSON.GeometryCollection
        if (!geometries || !geometries.length) {
          returnFeature.properties.area = 'No area'
          returnFeature.properties.id = i
          returnFeature.properties.featureGroup = getPosibleFolderId(returnFeature.properties)
          break
        }

        try {
          const validGeometries = geometries.filter(
            (geom): geom is GeoJSON.Polygon | GeoJSON.MultiPolygon =>
              geom?.type === 'Polygon' || geom?.type === 'MultiPolygon',
          )

          if (validGeometries.length === 0) {
            console.warn('No valid Polygon or MultiPolygon geometries found')
            returnFeature.properties.area = 'No valid geometries'
            returnFeature.properties.id = i
            returnFeature.properties.featureGroup = getPosibleFolderId(returnFeature.properties)

            return returnFeature
          }

          const turfUnion = validGeometries.reduce(
            (
              a: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>,
              b: GeoJSON.Polygon | GeoJSON.MultiPolygon,
            ) => {
              if (!a || !a.type || !b || !b.type) {
                return a || turf.feature(b)
              }
              return turf.union(a, turf.feature(b))
            },
            turf.feature(validGeometries[0]),
          )

          if (turfUnion) {
            turfUnion.properties = {
              ...returnFeature.properties,
              name: returnFeature.properties.name,
              area: GeoJSON.readableArea(GeoJSON.area(returnFeature)),
              id: i,
            }
            returnFeature = turfUnion
          } else {
            returnFeature.properties.area = 'No area'
            returnFeature.properties.id = i
          }
          returnFeature.properties.featureGroup = getPosibleFolderId(returnFeature.properties)
        } catch (error) {
          console.error('Error during geometry union:', error)
          returnFeature.properties.area = 'Error calculating area'
          returnFeature.properties.id = i
          returnFeature.properties.featureGroup = getPosibleFolderId(returnFeature.properties)
        }

        break
      }
      default:
        return null
    }
    return returnFeature
  })

  // Geometries the app cannot map (LineString, MultiPoint...) come back as null from the switch
  // above. Dropping them keeps the rest of the file importable — dereferencing them here used to
  // abort the entire import with a generic "el archivo puede tener errores de carga" message.
  featuresToAssign
    .filter(Boolean)
    .forEach(fta => folders[fta.properties.featureGroup].features.push(fta))

  const foldersArray = Object.keys(folders).map(folderName => {
    const obj = {
      name: folderName as FolderName,
      features: folders[folderName].features,
    }
    return obj
  })
  return foldersArray
}

export const getFeaturesFromKML = ({
  data,
  handlePerimeter,
  handlePaddocks,
  handleSamplingAreas,
  handleMonitoringSites,
  handleUnassigned,
  handleUnassignedPoints,
  handleExclusionAreas,
}: {
  data: string
  handlePerimeter?: (features: GeoJSON.Feature[]) => void
  handlePaddocks?: (features: GeoJSON.Feature[]) => void
  handleSamplingAreas?: (features: GeoJSON.Feature[]) => void
  handleMonitoringSites?: (features: GeoJSON.Point[]) => void
  handleUnassigned?: (features: GeoJSON.Feature[]) => void
  handleUnassignedPoints?: (features: GeoJSON.Point[]) => void
  handleExclusionAreas?: (features: GeoJSON.Feature[]) => void
}): void => {
  try {
    const parser = new XMLParser()
    const json = parser?.parse(data)

    let folders: FolderArray | null = null

    folders = plantillaParsing(json)

    if (!folders) {
      try {
        folders = getParsingFromOmnivore(data)
      } catch (error) {
        throw new errors.UnexpectedEntityError({
          message:
            'El archivo KML/KMZ utilizado puede tener errores de carga, verificar que el archivo no posea capas vacías.',
        })
      }

      folders = folders.map(folder => {
        if (!folder?.features?.length) return folder

        const validFeatures = folder.features.filter((feature): feature is GeoJSON.Feature => {
          if ('type' in feature && feature.type === 'Point') return true

          if (feature.geometry?.type === 'Polygon') {
            const coordinates = feature.geometry.coordinates[0]
            return (
              coordinates.length >= 4 &&
              coordinates.every(coord => Array.isArray(coord) && coord.length >= 2) &&
              coordinates[0].every((val, i) => val === coordinates[coordinates.length - 1][i])
            )
          }
          return true
        })

        return {
          ...folder,
          features: validFeatures,
        }
      }) as FolderArray
    }

    folders.forEach(folder => {
      if (folder.features?.length > 0) {
        switch (folder.name) {
          case 'perimeter':
            handlePerimeter?.(folder.features as GeoJSON.Feature[])
            break
          case 'paddocks':
            handlePaddocks?.(sortKMLFeaturesByArea(folder.features))
            break
          case 'estratas': {
            const newSamplingAreas = sortKMLFeaturesByArea(folder.features)

            // Agrega color inicial en HUE de GREEN
            const coloredSamplingAreas = newSamplingAreas.map(feature => {
              const color = getRandomColor()
              const newFeature = structuredClone(feature)
              newFeature.properties.color = color
              return newFeature
            })
            handleSamplingAreas?.(coloredSamplingAreas)
            break
          }
          case 'monitoringSites':
            handleMonitoringSites?.(folder.features as GeoJSON.Point[])
            break
          case 'exclusion': {
            const newExclusionAreas = sortKMLFeaturesByArea(folder.features)
            handleExclusionAreas?.(newExclusionAreas)
            break
          }
          case 'unassigned': {
            const newUnassigned = sortKMLFeaturesByArea(folder.features)
            handleUnassigned?.(newUnassigned)
            break
          }
          case 'unassignedPoints':
            handleUnassignedPoints?.(folder.features as GeoJSON.Point[])
            break
          default:
            break
        }
      }
    })
  } catch (error) {
    if (error instanceof errors.RuutsError) {
      throw error
    }
    if (error instanceof Error) {
      throw new errors.UnexpectedEntityError({ message: error.message })
    }
    throw new errors.UnexpectedEntityError({ message: 'Error desconocido' })
  }
}
